import { Box, Modal, Stack } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { IconButton } from 'utoniq-ui'

import { Resource } from '@/clients/utoniq-core/schema'
import { Loading } from '@/components/organisms/loading'
import { TheoPlayerView } from '@/components/organisms/resourcePlayer/TheoPlayerView'
import { styles } from '@/components/styles'
import { useDRMPlayer } from '@/core/resources/useDRMPlayer'

import { VideoPlayerControl } from './VideoPlayerControl'

type Props = {
  resource?: Resource
  autoplay?: boolean
  open: boolean
  onClose: () => void
}

export const VideoDRMPlayer = ({
  resource,
  autoplay,
  open,
  onClose,
}: Props) => {
  return (
    <Modal
      // NOTE: DRMの読み込みにrefを参照する必要があるため、モーダル内部の要素は常に出力し、スタイルで表示を制御する
      sx={{ display: open ? 'block' : 'none' }}
      open
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          outline: 'none',
          width: '100%',
          height: '100%',
          bgcolor: 'black',
        }}
      >
        <VideoDRMPlayerContent
          resource={resource}
          autoplay={autoplay}
          onClose={onClose}
        />
      </Box>
    </Modal>
  )
}

const VideoDRMPlayerContent = ({
  resource,
  autoplay,
  onClose,
}: {
  resource?: Resource
  autoplay?: boolean
  onClose: () => void
}) => {
  const [slidingValue, setSlidingValue] = useState(0)

  const { ref, player, currentTime, accessToken } = useDRMPlayer({
    source: {
      dashUri: resource?.dashUri || '',
      hlsUri: resource?.hlsUri || '',
    },
    opts: {
      chromeless: true,
    },
  })
  useEffect(() => {
    if (!(player && accessToken)) return

    player?.play()
    if (!autoplay) player.pause()
  }, [player, autoplay, accessToken])

  const handleClose = useCallback(() => {
    player?.pause()
    onClose()
  }, [onClose, player])

  return (
    <>
      <Stack
        sx={{
          position: 'relative',
          height: '100%',
          display: 'flex',
        }}
        flex={1}
      >
        {(!player || !player?.duration) && (
          <Box
            sx={{
              position: 'absolute',
              zIndex: 2,
              inset: 0,
              margin: 'auto',
              width: 'fit-content',
              height: 'fit-content',
              pointerEvents: 'none',
            }}
          >
            <Loading />
          </Box>
        )}
        <TheoPlayerView ref={ref} chromeless type="video" />
      </Stack>
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          pt: styles.safeAreaTop,
        }}
      >
        <IconButton.Close
          size="medium"
          bgcolor="grey.50"
          onClick={handleClose}
        />
      </Box>
      {player && (
        <VideoPlayerControl
          sliderValue={slidingValue ? slidingValue : currentTime}
          onChange={(_, time) => {
            if (typeof time === 'number') setSlidingValue(time)
          }}
          onChangeCommitted={(_, time) => {
            if (typeof time === 'number') player.currentTime = time
            setSlidingValue(0)
          }}
          onRewind={() => {
            player.currentTime = player.currentTime - 10
          }}
          onForward={() => {
            player.currentTime = player.currentTime + 10
          }}
          onPause={() => {
            player.pause()
          }}
          onPlay={() => {
            player.paused ? player.play() : player.pause()
          }}
          isPlaying={!player?.paused}
          duration={player?.duration}
        />
      )}
    </>
  )
}
